import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { colors } from '../styles/shared.js';
import { stageComment } from '../i18n.js';
import { cmdDismissModal } from '../commands.js';

import './stage-info.js';
import './nav-hints.js';
import './hamburger-button.js';
import './help-button.js';
import './touch-menu-overlay.js';
import './controls-overlay.js';
import './comment-modal.js';
import './confirm-reset-dialog.js';

@customElement('play-hud')
export class PlayHud extends LitElement {
  static styles = [
    colors,
    css`
      :host {
        position: absolute;
        inset: 0;
        pointer-events: none;
        display: flex;
        flex-direction: column;
      }
      .overlay-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.85);
        pointer-events: auto;
      }
    `,
  ];

  @property() accessor stageName = '';
  @property() accessor messageKey: string | null = null;
  @property({ type: Boolean }) accessor isTouch = false;
  @property({ type: Number }) accessor aspect = 1.33;
  @property({ type: Boolean }) accessor canGoPrev = false;
  @property({ type: Boolean }) accessor canGoNext = false;
  @property({ type: Boolean }) accessor stageProgressed = false;
  @property({ type: Boolean }) accessor commentAutoShown = false;

  // Overlay states
  @property({ type: Boolean }) accessor touchMenuOpen = false;
  @property({ type: Boolean }) accessor touchControlsOpen = false;
  @property({ type: Boolean }) accessor desktopControlsOpen = false;
  @property({ type: Boolean }) accessor commentModalOpen = false;
  @property({ type: Boolean }) accessor confirmReset = false;
  @property({ type: Number }) accessor confirmSelection = 0;

  private get _hasOverlay(): boolean {
    return (
      this.commentModalOpen ||
      this.touchControlsOpen ||
      this.desktopControlsOpen ||
      this.touchMenuOpen ||
      this.confirmReset
    );
  }

  render() {
    // Overlay rendering (mutually exclusive, same priority as Rust)
    if (this.isTouch && this.commentModalOpen) {
      return html`
        <div class="overlay-backdrop" @click=${cmdDismissModal}></div>
        <comment-modal .stageName=${this.stageName} .isTouch=${this.isTouch}></comment-modal>
      `;
    }
    if (this.touchControlsOpen) {
      return html`
        <div class="overlay-backdrop"></div>
        <controls-overlay variant="touch"></controls-overlay>
      `;
    }
    if (this.desktopControlsOpen) {
      return html`
        <div class="overlay-backdrop"></div>
        <controls-overlay variant="desktop"></controls-overlay>
      `;
    }
    if (this.touchMenuOpen) {
      return html`
        <div class="overlay-backdrop"></div>
        <touch-menu-overlay
          .canGoPrev=${this.canGoPrev}
          .canGoNext=${this.canGoNext}
          .stageProgressed=${this.stageProgressed}
        ></touch-menu-overlay>
      `;
    }
    if (this.confirmReset) {
      return html`
        <div class="overlay-backdrop"></div>
        <confirm-reset-dialog .selection=${this.confirmSelection}></confirm-reset-dialog>
      `;
    }

    // Normal HUD
    return html`
      <stage-info
        .stageName=${this.stageName}
        .messageKey=${this.messageKey}
        .isTouch=${this.isTouch}
        .aspect=${this.aspect}
      ></stage-info>
      ${this.isTouch
        ? html`
            <hamburger-button></hamburger-button>
            ${this.commentAutoShown && stageComment(this.stageName, true)
              ? html`<help-button></help-button>`
              : nothing}
          `
        : html`
            <nav-hints
              .canGoPrev=${this.canGoPrev}
              .canGoNext=${this.canGoNext}
            ></nav-hints>
          `}
    `;
  }
}
